
import { useState, useCallback } from 'react';
import { AffiliateDataMigrationService } from '@/services/affiliateDataMigrationService';
import { userAssignmentService } from '@/services/userAssignmentService';
import { toast } from 'sonner';

interface LogEntry {
  id: string;
  timestamp: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

export const useAffiliateBackfill = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const addLog = useCallback((type: LogEntry['type'], message: string) => {
    const logEntry: LogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      message
    };
    
    setLogs(prev => [...prev, logEntry]);
    console.log(`[${type.toUpperCase()}] ${message}`);
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setProgress(0);
  }, []);

  const exportLogs = useCallback(() => {
    const logText = logs.map(log => 
      `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-backfill-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Logs exported successfully');
  }, [logs]);

  const runFullMigration = useCallback(async () => {
    if (isRunning) return;

    setIsRunning(true);
    setProgress(0);
    
    try {
      addLog('info', '🚀 Starting full affiliate data migration...');
      
      // Phase 1: Backfill affiliates.user_id (25%)
      addLog('info', '🆔 Phase 1: Backfilling affiliates.user_id...');
      setProgress(10);
      
      const userIdResult = await AffiliateDataMigrationService.backfillAffiliatesUserId();
      if (userIdResult.success) {
        addLog('success', `✅ User ID backfill completed: ${userIdResult.updated} records updated`);
      } else {
        addLog('error', `❌ User ID backfill failed: ${userIdResult.errors.join(', ')}`);
      }
      
      setProgress(25);
      
      // Phase 2: Sync referred_by_affiliate (50%)
      addLog('info', '📋 Phase 2: Syncing referred_by_affiliate...');
      
      const syncResult = await AffiliateDataMigrationService.syncReferredByAffiliate();
      if (syncResult.success) {
        addLog('success', `✅ Sync completed: ${syncResult.updated} records updated`);
      } else {
        addLog('error', `❌ Sync failed: ${syncResult.errors.join(', ')}`);
      }
      
      setProgress(50);
      
      // Phase 3: Backfill user limits (75%)
      addLog('info', '👥 Phase 3: Backfilling user limits...');
      
      const backfillResult = await AffiliateDataMigrationService.backfillUserLimits();
      if (backfillResult.success) {
        addLog('success', `✅ Backfill completed: ${backfillResult.created} records created`);
      } else {
        addLog('error', `❌ Backfill failed: ${backfillResult.errors.join(', ')}`);
      }
      
      setProgress(75);
      
      // Phase 4: Validate consistency (100%)
      addLog('info', '🔍 Phase 4: Validating data consistency...');
      
      const consistencyResult = await userAssignmentService.validateDataConsistency();
      if (consistencyResult.isConsistent) {
        addLog('success', '✅ Data consistency validation passed');
      } else {
        addLog('warning', `⚠️ Found ${consistencyResult.issues.length} consistency issues`);
        consistencyResult.issues.forEach(issue => {
          addLog('warning', `   • ${issue}`);
        });
      }
      
      setProgress(100);
      
      const overallSuccess = userIdResult.success && syncResult.success && backfillResult.success;
      if (overallSuccess) {
        addLog('success', '🎉 Full migration completed successfully!');
        toast.success('Full migration completed successfully');
      } else {
        addLog('warning', '⚠️ Migration completed with some issues');
        toast.warning('Migration completed with some issues');
      }
      
    } catch (error) {
      addLog('error', `❌ Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      toast.error('Migration failed');
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, addLog]);

  const runUserIdBackfillOnly = useCallback(async () => {
    if (isRunning) return;

    setIsRunning(true);
    setProgress(0);
    
    try {
      addLog('info', '🆔 Starting backfill of affiliates.user_id...');
      setProgress(20);
      
      const result = await AffiliateDataMigrationService.backfillAffiliatesUserId();
      setProgress(100);
      
      if (result.success) {
        addLog('success', `✅ User ID backfill completed successfully: ${result.updated} records updated`);
        toast.success(`User ID backfill completed: ${result.updated} records updated`);
      } else {
        addLog('error', `❌ User ID backfill failed: ${result.errors.join(', ')}`);
        toast.error('User ID backfill operation failed');
      }
      
    } catch (error) {
      addLog('error', `❌ User ID backfill failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      toast.error('User ID backfill operation failed');
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, addLog]);

  const runSyncOnly = useCallback(async () => {
    if (isRunning) return;

    setIsRunning(true);
    setProgress(0);
    
    try {
      addLog('info', '🔄 Starting sync of referred_by_affiliate...');
      setProgress(20);
      
      const result = await AffiliateDataMigrationService.syncReferredByAffiliate();
      setProgress(100);
      
      if (result.success) {
        addLog('success', `✅ Sync completed successfully: ${result.updated} records updated`);
        if (result.errors.length > 0) {
          addLog('warning', `⚠️ Some errors occurred: ${result.errors.join(', ')}`);
        }
        toast.success(`Sync completed: ${result.updated} records updated`);
      } else {
        addLog('error', `❌ Sync failed: ${result.errors.join(', ')}`);
        toast.error('Sync operation failed');
      }
      
    } catch (error) {
      addLog('error', `❌ Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      toast.error('Sync operation failed');
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, addLog]);

  const runBackfillOnly = useCallback(async () => {
    if (isRunning) return;

    setIsRunning(true);
    setProgress(0);
    
    try {
      addLog('info', '👥 Starting backfill of user limits...');
      setProgress(20);
      
      const result = await AffiliateDataMigrationService.backfillUserLimits();
      setProgress(100);
      
      if (result.success) {
        addLog('success', `✅ Backfill completed successfully: ${result.created} records created`);
        if (result.errors.length > 0) {
          addLog('warning', `⚠️ Some errors occurred: ${result.errors.join(', ')}`);
        }
        toast.success(`Backfill completed: ${result.created} records created`);
      } else {
        addLog('error', `❌ Backfill failed: ${result.errors.join(', ')}`);
        toast.error('Backfill operation failed');
      }
      
    } catch (error) {
      addLog('error', `❌ Backfill failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      toast.error('Backfill operation failed');
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, addLog]);

  const runValidationOnly = useCallback(async () => {
    if (isRunning) return;

    setIsRunning(true);
    setProgress(0);
    
    try {
      addLog('info', '🔍 Starting data consistency validation...');
      setProgress(20);
      
      const result = await userAssignmentService.validateDataConsistency();
      setProgress(100);
      
      if (result.isConsistent) {
        addLog('success', '✅ Data consistency validation passed - no issues found');
        toast.success('Data is consistent');
      } else {
        addLog('warning', `⚠️ Found ${result.issues.length} consistency issues:`);
        result.issues.forEach(issue => {
          addLog('warning', `   • ${issue}`);
        });
        toast.warning(`Found ${result.issues.length} consistency issues`);
      }
      
    } catch (error) {
      addLog('error', `❌ Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      toast.error('Validation failed');
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, addLog]);

  return {
    logs,
    isRunning,
    progress,
    runFullMigration,
    runUserIdBackfillOnly,
    runSyncOnly,
    runBackfillOnly,
    runValidationOnly,
    clearLogs,
    exportLogs
  };
};
